import React, { useRef } from 'react'
import styled from 'styled-components'
import { useDrag, useDrop } from 'react-dnd'

interface Props {
  children: JSX.Element
  id: number
  index: number
  moveItem: (arg0: number | string, arg1: number | string) => void
}

const ItemTypes = {
  ITEM: 'item',
}

export default function DraggableContainer({ children, id, index, moveItem }: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null)
  const [{ handlerId }, drop] = useDrop({
    accept: ItemTypes.ITEM,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item: any, monitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      // Determine mouse position
      const clientOffset = monitor.getClientOffset()
      // Get pixels to the top
      const hoverClientY = clientOffset && clientOffset.y - hoverBoundingRect.top

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      const isNotMovableDownwards = hoverClientY && dragIndex < hoverIndex && hoverClientY < hoverMiddleY
      const isNotMovableUpwards = hoverClientY && dragIndex > hoverIndex && hoverClientY > hoverMiddleY

      if (isNotMovableDownwards || isNotMovableUpwards) {
        return
      }

      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })
  const [, drag] = useDrag({
    type: ItemTypes.ITEM,
    item: () => {
      return { id, index }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  return (
    <Draggable draggable ref={ref} data-handler-id={handlerId}>
      <Container>
        <DragIcon />
        {children}
      </Container>
    </Draggable>
  )
}

const Draggable = styled.div`
  cursor: move;
  width: 100%;
`

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  & > *:nth-of-type(2) {
    flex: 1;
  }
`

const DragIcon = styled.div`
  height: 50px;
  position: relative;
  margin-right: 20px;

  &:before {
    content: '.';
    position: absolute;
    left: 0;
    top: 5px;
    font-size: 20px;
    line-height: 20px;
    color: ${(props) => props.theme.customPalette.darkGray};
    text-shadow:
      0 5px currentcolor,
      0 10px currentcolor,
      5px 0 currentcolor,
      5px 5px currentcolor,
      5px 10px currentcolor;
  }
`
